// Where a signed-in person lands. Not authored surface: the box restores this file from the
// template on every sync, and `isAgentAppWritable` refuses a write under `src/lib/platform/`.
//
// Deliberately NOT in `auth.ts`, which says outright that it "has no opinion about what [the
// sign-in and sign-up pages] look like, which routes are public, or where anyone lands". That
// stands: WHERE anyone lands is the author's decision, written down in their own `entry` export.
// What lives here is the one step after the decision — reading that table the same way in every
// place the app reads it.
//
// It is shipped rather than described because the description was already there and the app read
// it two ways in the same tree. On machine s4464pv79 the public-route guard redirected a signed-in
// visitor to `entry.default` and never looked at `byRole`, while the sign-in page navigated to a
// hardcoded `'/plots'` that never looked at `entry` at all — so a coordinator whose own published
// inventory declared `byRole.coordinator = '/claims'` signed in and landed on somebody else's
// screen. Nothing anywhere disagreed, because `entry` had no reader: declaring a route in it
// bought silence. One function with one rule is the fix; a longer paragraph is not.

import { getSession, refreshSession, type SessionInfo, signOut } from './auth';

/**
 * The author's landing table, taken STRUCTURALLY. `src/routes.tsx` names this type itself
 * (`AppEntry`, wherever the author declares it) and the platform never imports it — the same way
 * the route inventory is a shape the app mirrors rather than a package it depends on.
 */
export interface AppEntryRoutes {
  /**
   * OPTIONAL, because there are apps with no honest value for it. In one where every person the
   * design named is `serving` — a role the owner grants — every screen is role-gated, so every listed
   * route refuses somebody holding nothing; and `/`, `/sign-in` and `/sign-up` are exactly the routes
   * the app's own root guard sends a signed-in visitor away from, so naming one is a redirect into
   * itself. Requiring a value is what made authors aim it at a staff screen and hand every fresh
   * sign-up a refusal to look at. Omitted, the landing is where a role-less person goes — and the
   * platform's access gate is what renders there for them.
   */
  default?: string;
  byRole: Record<string, string>;
}

/**
 * Where THIS person lands: the first key of `byRole`, **in the order the author wrote them**, that
 * the person holds — and `default` when they hold none of them.
 *
 * The iteration order is the whole decision, and it is not the obvious one. Iterating the SESSION's
 * roles instead would seat people alphabetically: roles come back from the server comma-joined and
 * already sorted, so "the first role this person has" means the first slug in the alphabet — a
 * landing nobody chose, and one that silently MOVES the day a role is renamed. Worse, a preview
 * session carries `admin` alongside every app role, and `admin` sorts ahead of almost anything, so
 * under session order every builder previewing their own product would be sent wherever the
 * alphabet put them rather than into their product. `byRole`'s own key order is the only order in
 * this app written down by somebody who knows what it is for, so that is the order that decides.
 *
 * `admin` is the PLATFORM's role and belongs in no `byRole`: it says nothing about which of this
 * product's screens comes first, and naming it there hands the app's first screen to the alphabet
 * again.
 *
 * NO DEFAULT MEANS THE LANDING, never a failure. `/` is the one address this file can name knowing
 * nothing about the product, and it is where an app that declares no default has to send a person no
 * `byRole` key matches — in such an app there is no screen they may see, so the honest answer is a
 * state rather than an address, and the access gate in `src/main.tsx` renders it.
 */
export function resolveEntryRoute(entry: AppEntryRoutes, roles: readonly string[]): string {
  const held = new Set(roles);
  for (const [role, route] of Object.entries(entry.byRole)) {
    if (held.has(role)) return route;
  }
  return entry.default ?? '/';
}

const PLATFORM_ADMIN_ROLE = 'admin';

export function canEnterSurface(required: readonly string[], roles: readonly string[]): boolean {
  if (required.length === 0) return true;
  const held = new Set(roles);
  if (held.has(PLATFORM_ADMIN_ROLE)) return true;
  return required.some((role) => held.has(role));
}

export type SurfaceEntryVerdict = { allowed: true } | { allowed: false; to: string };

/**
 * `isPublic` — WHETHER A SIGNED-OUT VISITOR MAY STAND HERE, and the only thing that opens the line
 * below. It is not the author's word and not this file's guess: it is the platform's own per-surface
 * derivation, emitted beside the role requirements and rewritten by every sync, so a screen is open
 * here exactly when the app's own contract says every read on it needs no session.
 *
 * OPTIONAL AND DEFAULTED CLOSED, which is what keeps the fact one-directional. Before this the
 * anonymous bounce fired unconditionally and BEFORE `required` was ever read, so every route of
 * every app with accounts turned a first-time visitor away — the app's own front door included. The
 * fix opens exactly the surfaces the derivation named and nothing else: a caller that says nothing
 * keeps the bounce it already had, so no screen can open by a call site forgetting the flag.
 */
export function decideSurfaceEntry(
  session: SessionInfo,
  required: readonly string[],
  entry: AppEntryRoutes,
  currentPath: string,
  isPublic = false,
): SurfaceEntryVerdict {
  if (session.status === 'anonymous') return isPublic ? { allowed: true } : { allowed: false, to: '/sign-in' };
  if (session.status !== 'authed') return { allowed: true };
  const roles = session.user?.roles ?? [];
  if (canEnterSurface(required, roles)) return { allowed: true };
  const to = resolveEntryRoute(entry, roles);
  return to === currentPath ? { allowed: true } : { allowed: false, to };
}

/**
 * Crossing INTO the signed-in app, after `signInWithEmail` / `signUpWithEmail` /
 * `signInWithProvider` resolved `ok`. A full DOCUMENT load, and that is the whole point.
 *
 * The app's feature machines are started ONCE, when the actor system boots, and that boot happens
 * while the visitor is still anonymous — their reads run, come back unauthorised or empty, and the
 * machines settle into an error state. A soft `navigate()` changes which route renders and nothing
 * else: every one of those machines is still the actor that failed a minute ago, holding its
 * anonymous answer, so the first screen a person sees after signing in is the "we could not load
 * this" state of a read that was never retried. Nothing re-runs it — the machine is in a final-ish
 * state, and no fetch policy helps, because no fetch is ever issued.
 *
 * Assigning `window.location` throws the actor system away and builds a new one against a document
 * that already carries the session cookie, so every machine's FIRST read is the signed-in read.
 * That is a guarantee about the whole app rather than about the machines somebody remembered, which
 * is why it lives here and not in a rehydration convention each feature has to honour.
 *
 * The refresh comes first so the roles that pick the landing route are the ones the SERVER just
 * issued, not whatever this document published before the sign-in.
 */
export async function enterAfterAuth(entry: AppEntryRoutes): Promise<void> {
  await refreshSession();
  window.location.assign(resolveEntryRoute(entry, getSession().user?.roles ?? []));
}

/**
 * The server's own code for "you are signed in, and this is not yours". `denyView` throws it on a
 * role-scoped read (`UNAUTHENTICATED` is its other arm, for a visitor who is not signed in at all).
 */
const DENIED_READ_CODE = 'FORBIDDEN';

/** Once per document. The redirect below replaces the document, so this resets by construction; what
 *  it stops is a page that fired six refused reads at once queueing six navigations. */
let leavingDeniedRead = false;

/** The crossing leaves a note for the document it lands on, so a landing that is ITSELF refused
 *  cannot bounce straight back — the loop `enterAfterDeniedRead` cannot see from inside one document.
 *  Read once at boot and cleared, so it never outlives the arrival it describes. */
const ARRIVED_FROM_DENIED_READ = 'onauto:arrived-from-denied-read';
/** How long after boot a refusal still counts as the landing's own first reads. */
const LANDING_SETTLE_MS = 10_000;
const arrivedFromDeniedRead = ((): boolean => {
  try {
    const flagged = window.sessionStorage.getItem(ARRIVED_FROM_DENIED_READ) !== null;
    if (flagged) window.sessionStorage.removeItem(ARRIVED_FROM_DENIED_READ);
    return flagged;
  } catch {
    return false;
  }
})();
const documentBootedAt = typeof performance === 'undefined' ? 0 : performance.now();

/**
 * THE SERVER IS THE ONE AUTHORITY ON WHO MAY SEE WHAT, and this is the client half of listening to it.
 *
 * A served person who types an operator's URL — a student opening the tutor's `/requests` — got the
 * operator's page rendered around a read the API had refused: a dead screen with an error on it and
 * no way back. Nothing on the client could have known better without a second copy of the
 * authorization rules, and a second copy is the thing that must never exist. So this does not
 * PREDICT the refusal, it reacts to the one the server already sent.
 *
 * ONLY A REFUSED READ ARRIVES HERE, and that is structural rather than a test this function
 * performs. A refused COMMAND cannot reach it: the generated mutation resolver RETURNS
 * `denied(...)` — a `MutationResponse { success: false, error: { code: 'FORBIDDEN' } }` carried in
 * the DATA payload — so it lands as an ordinary successful response and stays the mutation error the
 * page's own machine already reports. That is the right outcome: the person is standing somewhere
 * they are allowed to be and pressed a control that is not theirs, which is a message, not a move.
 * Only a refused QUERY throws (`denyView`, the gate prelude on a role-scoped view), and a page whose
 * data this person may not read is a page they should not be sitting on.
 *
 * `FORBIDDEN` and never `UNAUTHENTICATED`: the server tells "not allowed" apart from "not signed
 * in", and an anonymous visitor's refusal belongs to whatever sign-in guard the app already wrote —
 * sending them to a landing route would bounce them off the page about to ask them to sign in.
 *
 * IT DOES NOT CATCH EVERYTHING and must not pretend to. A scoped view (`scopeAndMask`, `scopeOne`)
 * answers an empty list or a null row instead of throwing, so a page this person may open but has no
 * rows on stays an honest empty page. Only the views the codegen gates produce this signal at all.
 *
 * `/` rather than a computed route, so this needs no `entry`: the app's own root already sends a
 * signed-in visitor through `resolveEntryRoute(entry, roles)`, which makes `/` the one destination
 * this file can name while knowing nothing about the product. A full document load for exactly the
 * reason `enterAfterAuth` uses one — every machine in this app booted against a page this person may
 * not read, and a soft navigation would leave all of them holding that refusal.
 */
export function enterAfterDeniedRead(codes: readonly string[]): void {
  if (leavingDeniedRead) return;
  if (!codes.includes(DENIED_READ_CODE)) return;
  if (getSession().status !== 'authed') return;
  if (window.location.pathname === '/') return;
  // THE LANDING IS WHERE THE APP PUT THIS PERSON. A refusal in the first seconds after arriving from a
  // refusal is the landing's own read being refused too (measured: a student's home page wired to an
  // operator's query) — crossing again would bounce `/subjects` → `/` → `/available-slots` → `/` forever.
  // The page keeps its own empty or error state instead; the person is where the app sends them.
  if (arrivedFromDeniedRead && performance.now() - documentBootedAt < LANDING_SETTLE_MS) return;
  leavingDeniedRead = true;
  try {
    window.sessionStorage.setItem(ARRIVED_FROM_DENIED_READ, '1');
  } catch {
    // No storage (a private window that refuses it): the crossing still happens; only the loop guard is lost.
  }
  window.location.assign('/');
}

/**
 * Crossing back OUT, from whatever control the app offers. The mirror of `enterAfterAuth`: the
 * document reloads public, so no machine is left holding a signed-in person's rows.
 *
 * The `await` is load-bearing. Firing `signOut()` and the navigation together races the document
 * teardown against the `POST /api/auth/sign-out` that clears the cookie: the browser cancels
 * in-flight requests when it leaves, so the sign-out can lose, and the person lands on `/` still
 * signed in. Sign-out only counts once the server has answered.
 */
export async function leaveAfterSignOut(): Promise<void> {
  await signOut();
  window.location.assign('/');
}
